import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  IconButton, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Fullscreen as FullscreenIcon, 
  FullscreenExit as FullscreenExitIcon,
  GridView as GridViewIcon
} from '@mui/icons-material';
import CCTVFeed from '../components/cctv/CctvFeed';

// 전체화면 모드 관련 interface
interface FullscreenDocument extends Document {
  mozFullScreenElement?: Element;
  msFullscreenElement?: Element;
  webkitFullscreenElement?: Element;
  msExitFullscreen?: () => Promise<void>;
  mozCancelFullScreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void>;
}

// 간소화된 CCTV 타입 (필요한 속성만 포함)
interface SimplifiedCctv {
  id: number;
  pool_id: number;
  name: string;
  location: string;
  status: string;
  camera_type: 'safety' | 'management';
  url?: string;
}

// 그리드 레이아웃 옵션
type GridLayout = {
  rows: number;
  columns: number;
  label: string;
};

const GRID_LAYOUTS: GridLayout[] = [
  { rows: 1, columns: 1, label: '1 x 1' },
  { rows: 1, columns: 2, label: '1 x 2' },
  { rows: 2, columns: 2, label: '2 x 2' },
  { rows: 2, columns: 3, label: '2 x 3' },
  { rows: 3, columns: 2, label: '3 x 2' },
  { rows: 3, columns: 3, label: '3 x 3' }
];

// 더미 CCTV 데이터 - CCTVFeed와 동일한 ID 구조를 가진 카메라 배열
const DUMMY_CAMERAS: SimplifiedCctv[] = [
  { 
    id: 1, 
    pool_id: 1, 
    name: '메인 풀', 
    url: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '메인 풀',
    camera_type: 'safety' // 안전 모니터링용 카메라
  },
  { 
    id: 2, 
    pool_id: 1, 
    name: '어린이 풀', 
    url: 'https://images.unsplash.com/photo-1576013551627-0ee20f8261c4?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '어린이 풀',
    camera_type: 'safety' // 안전 모니터링용 카메라
  },
  { 
    id: 3, 
    pool_id: 1, 
    name: '입구', 
    url: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '입구',
    camera_type: 'management' // 관리용 카메라
  },
  { 
    id: 4, 
    pool_id: 1, 
    name: '탈의실 입구', 
    url: 'https://images.unsplash.com/photo-1601962468178-9f84128bd046?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '탈의실',
    camera_type: 'management' // 관리용 카메라
  },
  { 
    id: 5, 
    pool_id: 1, 
    name: '깊은 시설', 
    url: 'https://images.unsplash.com/photo-1562863474-ea99ced3429c?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '수심 2m 구역',
    camera_type: 'safety' // 안전 모니터링용 카메라
  },
  { 
    id: 6, 
    pool_id: 1, 
    name: '다이빙 구역', 
    url: 'https://images.unsplash.com/photo-1576610616656-d3aa5d1f4534?q=80&w=1000&auto=format&fit=crop',
    status: 'active',
    location: '다이빙 구역',
    camera_type: 'safety' // 안전 모니터링용 카메라
  }
];

const CctvFullscreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [layoutIndex, setLayoutIndex] = useState<number>(2); // 기본값은 2x2
  const [viewMode, setViewMode] = useState<'all' | 'safety' | 'management'>('all');
  const [compactMode, setCompactMode] = useState<boolean>(true); // 기본값을 true로 변경하여 항상 여백 없이 표시
  const [cameras] = useState<SimplifiedCctv[]>(DUMMY_CAMERAS);
  const [selectedCameras, setSelectedCameras] = useState<SimplifiedCctv[]>(DUMMY_CAMERAS);

  // URL 파라미터에서 값 가져오기
  const poolIdParam = searchParams.get('poolId');
  const layoutParam = searchParams.get('layoutIndex');
  const viewModeParam = searchParams.get('viewMode');
  const compactModeParam = searchParams.get('compactMode');
  const cameraIdParam = searchParams.get('cameraId');
  const isDummyParam = searchParams.get('isDummy');
  const preserveOriginalRatioParam = searchParams.get('preserveOriginalRatio'); // 새 파라미터 추가
  const noGapParam = searchParams.get('noGap'); // 간격 제거 파라미터 추가
  
  // 파라미터 파싱 개선
  const poolId = poolIdParam ? parseInt(poolIdParam, 10) : 1; // 항상 유효한 풀 ID 사용
  const cameraId = cameraIdParam ? parseInt(cameraIdParam, 10) : null; // 선택된 카메라 ID
  const isDummy = isDummyParam === 'true'; // isDummy 파라미터 처리
  const preserveOriginalRatio = preserveOriginalRatioParam !== 'false'; // 기본값을 true로 변경하여 항상 원본 비율 유지
  const noGap = noGapParam === 'true'; // 간격 제거 파라미터 처리
  
  // URL 파라미터를 기반으로 초기 상태 설정
  useEffect(() => {
    if (layoutParam) {
      setLayoutIndex(parseInt(layoutParam, 10));
    }
    
    if (viewModeParam && ['all', 'safety', 'management'].includes(viewModeParam)) {
      setViewMode(viewModeParam as 'all' | 'safety' | 'management');
    }
    
    if (compactModeParam) {
      setCompactMode(compactModeParam === 'true');
    } else {
      // compactMode 파라미터가 없으면 기본값을 true로 설정
      setCompactMode(true);
    }
    
    // 항상 기본 필터 적용 (모든 카메라에 필터 적용)
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutParam, viewModeParam, compactModeParam]);
  
  // 필터 적용 함수
  const applyFilters = () => {
    let filteredCameras = [...DUMMY_CAMERAS];
    
    // 더미 데이터에 URL 추가
    if (isDummy) {
      filteredCameras = DUMMY_CAMERAS.map(camera => ({
        ...camera,
        url: camera.url || `https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?q=80&w=1000&auto=format&fit=crop`
      }));
    }
    
    // 카메라 타입 필터링
    if (viewMode !== 'all') {
      filteredCameras = filteredCameras.filter(camera => camera.camera_type === viewMode);
    }
    
    setSelectedCameras(filteredCameras);
  };
  
  // 필터 변경시 적용
  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // 컴포넌트 마운트 시 자동으로 전체화면 모드 시도 (선택 사항, 현재는 주석 처리된 로직 활용 가능)
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
        console.error('전체화면 모드 진입 실패:', error);
      }
    };

    // 페이지 로드 후 약간의 지연 시간을 두고 전체화면 시도
    const timer = setTimeout(() => {
      enterFullscreen();
    }, 1000);

    // 전체화면 상태 변경 이벤트 리스너
    const handleFullscreenChange = () => {
      const doc = document as FullscreenDocument;
      const isInFullscreen = !!(
        doc.fullscreenElement || 
        doc.mozFullScreenElement || 
        doc.webkitFullscreenElement || 
        doc.msFullscreenElement
      );
      setIsFullscreen(isInFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // 전체화면 토글 함수
  const toggleFullscreen = async () => {
    const doc = document as FullscreenDocument;
    
    if (!doc.fullscreenElement && !doc.mozFullScreenElement && 
        !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (error) {
        console.error('전체화면 모드 진입 실패:', error);
      }
    } else {
      try {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
      } catch (error) {
        console.error('전체화면 모드 종료 실패:', error);
      }
    }
  };

  // 창 닫기 함수
  const handleClose = () => {
    window.close();
  };
  
  // 레이아웃 변경 핸들러
  const handleLayoutChange = (event: SelectChangeEvent<number>) => {
    setLayoutIndex(Number(event.target.value));
  };
  
  // 뷰 모드 변경 핸들러
  const handleViewModeChange = (event: SelectChangeEvent<string>) => {
    setViewMode(event.target.value as 'all' | 'safety' | 'management');
  };
  
  // 여백 모드 변경 핸들러
  const handleCompactModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompactMode(event.target.checked);
  };

  return (
    <Box 
      sx={{ 
        height: '100vh', 
        width: '100vw', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden', 
        bgcolor: 'black', // 배경을 검은색으로 설정하여 여백이 검은색으로 보이게 함
        m: 0, 
        p: 0
      }}
    >
      {/* 헤더 컨트롤 패널 */}
      <Box 
        sx={{ 
          p: 1, 
          bgcolor: 'background.paper', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          height: '40px', // 헤더 높이 더 축소
          minHeight: '40px',
          boxSizing: 'border-box',
          // 간격을 완전히 없애기 위한 스타일
          m: 0,
          borderRadius: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            CCTV 모니터링
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="camera-layout-label">그리드 레이아웃</InputLabel>
            <Select
              labelId="camera-layout-label"
              id="camera-layout"
              value={layoutIndex}
              label="그리드 레이아웃"
              onChange={handleLayoutChange}
            >
              {GRID_LAYOUTS.map((layout, index) => (
                <MenuItem key={index} value={index}>
                  {layout.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="camera-view-mode-label">카메라 타입</InputLabel>
            <Select
              labelId="camera-view-mode-label"
              id="camera-view-mode"
              value={viewMode}
              label="카메라 타입"
              onChange={handleViewModeChange}
            >
              <MenuItem value="all">전체 카메라</MenuItem>
              <MenuItem value="safety">안전 모니터링</MenuItem>
              <MenuItem value="management">시설 관리</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch 
                checked={compactMode} 
                onChange={handleCompactModeChange}
                size="small"
              />
            }
            label="여백 없이 보기"
            sx={{ ml: 1 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={toggleFullscreen} 
            sx={{ mr: 1 }}
            title={isFullscreen ? '전체화면 종료' : '전체화면 모드'}
            aria-label={isFullscreen ? '전체화면 종료' : '전체화면 모드'}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          
          <IconButton 
            onClick={handleClose}
            title="닫기"
            aria-label="닫기"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* 메인 콘텐츠 영역 */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          p: 0, // 패딩 제거
          overflow: 'hidden',
          bgcolor: 'black' // 배경 컬러 변경
        }}
      >
        <Grid 
          container 
          spacing={0} // 항상 간격을 0으로 설정
          sx={{ 
            flexGrow: 1,
            m: 0, // 마진 제거
            p: 0, // 패딩 제거
            width: '100%', 
            height: '100%',
            borderSpacing: 0, // 추가: 테이블 간격 제거
            gap: 0, // 추가: 그리드 간격 제거
            // CSS Grid 레이아웃 사용
            display: 'grid',
            gridTemplateRows: `repeat(${GRID_LAYOUTS[layoutIndex].rows}, 1fr)`,
            gridTemplateColumns: `repeat(${GRID_LAYOUTS[layoutIndex].columns}, 1fr)`,
            '& > .MuiGrid-item': {
              p: 0, // 그리드 아이템 패딩 제거
              m: 0, // 그리드 아이템 마진 제거
              borderSpacing: 0, // 그리드 아이템 간격 제거
              border: 'none !important', // 테두리 제거
              height: '100%' // 높이 100%로 설정
            },
            // noGap이 true일 경우 추가 스타일 적용
            ...(noGap ? {
              gridGap: 0,
              gridColumnGap: 0,
              gridRowGap: 0,
              columnGap: 0,
              rowGap: 0
            } : {})
          }}
        >
          {selectedCameras.slice(0, GRID_LAYOUTS[layoutIndex].rows * GRID_LAYOUTS[layoutIndex].columns).map((camera, index) => {
            // 그리드 위치 계산
            const row = Math.floor(index / GRID_LAYOUTS[layoutIndex].columns);
            const col = index % GRID_LAYOUTS[layoutIndex].columns;
            
            return (
              <Grid 
                item 
                key={camera.id}
                xs={12 / GRID_LAYOUTS[layoutIndex].columns}
                sx={{ 
                  p: 0, // 패딩 제거
                  boxSizing: 'border-box',
                  position: 'relative',
                  border: 'none', // 테두리 제거
                  margin: 0, // 마진 제거
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                  // CSS Grid 항목 위치 지정
                  gridRow: row + 1,
                  gridColumn: col + 1,
                  height: '100%', // 높이 100%로 설정
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '0px solid black', // 테두리 제거
                    borderRadius: 0,
                    bgcolor: 'black',
                    padding: 0, // 패딩 제거
                    margin: 0 // 마진 제거
                  }}
                >
                  <CCTVFeed 
                    poolId={poolId} 
                    cameraId={camera.id} 
                    title="" 
                    compactMode={true}
                  />
                  
                  {/* 카메라 이름 오버레이 - 우측 상단으로 위치 변경 */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 10, 
                      right: 10, 
                      p: 0.5,
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '4px',
                      backdropFilter: 'blur(2px)',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      zIndex: 10
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white',
                        textShadow: '0px 1px 2px rgba(0,0,0,0.7)',
                        fontWeight: 'bold',
                        fontSize: '0.75rem'
                      }}
                    >
                      {camera.name}
                    </Typography>
                  </Box>
                  
                  {/* 개별 카메라 전체화면 버튼 */}
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      },
                      zIndex: 10,
                      width: '28px',
                      height: '28px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // ID로 해당 카메라의 비디오 요소를 찾아 전체화면으로 전환
                      const container = document.getElementById(`cctv-container-${camera.id}`);
                      if (container && document.fullscreenEnabled) {
                        if (!document.fullscreenElement) {
                          container.requestFullscreen().catch(err => {
                            console.error(`전체화면 전환 오류: ${err.message}`);
                          });
                        } else {
                          document.exitFullscreen();
                        }
                      }
                    }}
                    size="small"
                    aria-label="전체화면"
                    title="전체화면"
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      
      {/* 전체 페이지에 적용되는 스타일 */}
      <style>{`
        body, html {
          margin: 0;
          padding: 0;
          overflow: hidden;
          background-color: black;
        }
        
        /* CCTV 피드 컨테이너 스타일 */
        .cctv-feed-fullscreen {
          width: 100%;
          height: 100%;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        /* 간격 제거를 위한 MUI 그리드 스타일 오버라이드 */
        .MuiGrid-container {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          height: 100% !important;
          gap: 0 !important;
          border-spacing: 0 !important;
          grid-gap: 0 !important;
          column-gap: 0 !important;
          row-gap: 0 !important;
        }
        
        .MuiGrid-item {
          padding: 0 !important;
          margin: 0 !important;
          border: none !important;
          border-spacing: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
        }
        
        /* CCTV 컨테이너 스타일 */
        [id^="cctv-container"] {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          overflow: hidden !important;
        }
        
        /* 이미지 및 비디오 스타일 - 원본 비율 유지 */
        [id^="cctv-container"] img,
        [id^="cctv-container"] video {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important; /* fill에서 contain으로 변경 */
          object-position: center !important;
          display: block !important;
          background-color: black !important;
        }
        
        /* 테두리와 간격 제거를 위한 추가 스타일 */
        * {
          border: none !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
        }
        
        .MuiPaper-root {
          border-radius: 0 !important;
          box-shadow: none !important;
          background-color: black !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 드롭다운 메뉴 스타일 */
        .MuiMenuItem-root {
          background-color: #121212 !important;
          color: white !important;
          font-weight: 400 !important;
        }
        
        .MuiMenuItem-root:hover {
          background-color: #333333 !important;
        }
        
        .MuiList-root {
          background-color: #121212 !important;
          padding: 0 !important;
        }
        
        /* 그리드 간 여백 제거를 위한 추가 스타일 */
        .MuiGrid-root.MuiGrid-container {
          gap: 0 !important;
          grid-gap: 0 !important;
          column-gap: 0 !important;
          row-gap: 0 !important;
        }
        
        /* 그리드 아이템 사이의 경계선 제거 */
        .MuiGrid-item {
          border: none !important;
        }
        
        /* CCTV 컨테이너 테두리 제거 */
        .MuiBox-root {
          border: none !important;
        }
        
        /* 그리드 시스템 내부 간격 제거를 위한 추가 스타일 */
        .MuiGrid-spacing-xs-0 {
          margin: 0 !important;
          width: 100% !important;
          padding: 0 !important;
        }
        
        /* 중첩된 그리드 아이템도 간격 없게 설정 */
        .MuiGrid-item .MuiGrid-item {
          padding: 0 !important;
          margin: 0 !important;
        }

        /* 추가: 그리드 컨테이너 사이의 간격 제거 */
        .MuiGrid-container .MuiGrid-container {
          gap: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 부모/자식 그리드 간 여백 제거 */
        .MuiGrid-root {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        /* MUI 프레임워크에서 자동 생성하는 클래스에도 적용 */
        [class*="MuiGrid-grid-"] {
          padding: 0 !important;
          margin: 0 !important;
          border: none !important;
        }
      `}</style>
    </Box>
  );
};

export default CctvFullscreen; 